"use client";

import { useTranslations } from "next-intl";
import { ReactNode, useRef, useState } from "react";
import { Flash } from "./Flash";

export function useActionState() {
  const t = useTranslations("common");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const runningRef = useRef(false);

  async function run(action: () => Promise<void>, success?: string) {
    if (runningRef.current) {
      return;
    }

    runningRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);

    try {
      await action();
      if (success) {
        setMessage(success);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("requestFailed"));
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }

  async function runInteractive(action: () => Promise<void>) {
    setError(null);
    setMessage(null);

    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("requestFailed"));
    }
  }

  return { busy, error, message, run, runInteractive };
}

export function PageMessages({ error, message }: { error: string | null; message: string | null }) {
  return <Flash error={error} message={message} />;
}

export function EmptyState({ children }: { children?: ReactNode }) {
  const t = useTranslations("common");

  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <span className="mb-2 text-2xl">📭</span>
      <p className="text-sm text-text-muted">{children ?? t("emptyData")}</p>
    </div>
  );
}
